import CardContents from '@ifca-root/react-component/src/components/CardList/CardContents'
import { CommonDialog } from '@ifca-root/react-component/src/components/Dialog/CommonDialog'
import { MenuItem, TextField } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import React, { useState } from 'react'
import * as yup from 'yup'
import {
  payeeVerificationIDTypes,
  residentStatusOptions,
  bankCodeOptions,
  beneBankSetupOptions,
  paymentPurposeOptions,
} from './DummyDataEFTDetails'

interface PayeeEFTDetailsDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: (data: EFTDetailFormData) => void
  editData?: EFTDetailFormData
}

export interface EFTDetailFormData {
  payeeVerificationIDType: string
  residentStatus: string
  bankCode: string
  beneBankName: string
  beneficiaryBankAccount: string
  ibgBankCode: string
  defaultPaymentPurposeCode: string
}

const initialFormData: EFTDetailFormData = {
  payeeVerificationIDType: '',
  residentStatus: 'Y',
  bankCode: '',
  beneBankName: '',
  beneficiaryBankAccount: '',
  ibgBankCode: '',
  defaultPaymentPurposeCode: '',
}

const eftDetailSchema = yup.object().shape({
  payeeVerificationIDType: yup
    .string()
    .required('Payee Verification ID Type is required'),
  residentStatus: yup.string().required('Resident Status is required'),
  bankCode: yup.string().required('Bank Code is required'),
  beneBankName: yup.string().required('Bene Bank Name is required'),
  beneficiaryBankAccount: yup
    .string()
    .required('Beneficiary Bank Account is required'),
})

export const PayeeEFTDetailsDialog = (props: PayeeEFTDetailsDialogProps) => {
  const { open, onClose, onConfirm, editData } = props
  const [formData, setFormData] = useState<EFTDetailFormData>(editData ?? initialFormData)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const handleChange = (field: keyof EFTDetailFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleBeneBankChange = (value: string) => {
    const selectedBank = beneBankSetupOptions.find(b => b.value === value)
    setFormData(prev => ({
      ...prev,
      beneBankName: value,
      ibgBankCode: selectedBank?.ibgBankCode ?? '',
    }))
    if (errors.beneBankName) {
      setErrors(prev => ({ ...prev, beneBankName: '' }))
    }
  }

  const validate = (): boolean => {
    try {
      eftDetailSchema.validateSync(formData, { abortEarly: false })
      setErrors({})
      return true
    } catch (err) {
      const newErrors: Record<string, string> = {}
      if (err instanceof yup.ValidationError) {
        err.inner.forEach(e => {
          if (e.path) newErrors[e.path] = e.message
        })
      }
      setErrors(newErrors)
      return false
    }
  }

  return (
    <CommonDialog
      fullWidth={true}
      open={open}
      onClose={onClose}
      sections={{
        header: {
          title: 'EFT Details',
          rightText: editData ? 'Edit' : 'New',
        },
        body: () => (
          <CardContents>
            <Autocomplete
              options={payeeVerificationIDTypes}
              getOptionLabel={option => option?.label || ''}
              value={
                payeeVerificationIDTypes.find(
                  o => o.value === formData.payeeVerificationIDType
                ) || null
              }
              onChange={(_, newValue) =>
                handleChange(
                  'payeeVerificationIDType',
                  newValue?.value || ''
                )
              }
              fullWidth
              renderInput={params => (
                <TextField
                  {...params}
                  label="Payee Verification ID Type"
                  margin="dense"
                  required
                  helperText={errors.payeeVerificationIDType || ''}
                  error={!!errors.payeeVerificationIDType}
                />
              )}
            />

            <TextField
              select
              label="Resident Status"
              value={formData.residentStatus}
              onChange={e => handleChange('residentStatus', e.target.value)}
              fullWidth
              margin="dense"
              required
              helperText={errors.residentStatus || ''}
              error={!!errors.residentStatus}
            >
              {residentStatusOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <Autocomplete
              options={bankCodeOptions}
              getOptionLabel={option => option?.label || ''}
              value={
                bankCodeOptions.find(
                  o => o.value === formData.bankCode
                ) || null
              }
              onChange={(_, newValue) =>
                handleChange('bankCode', newValue?.value || '')
              }
              fullWidth
              renderInput={params => (
                <TextField
                  {...params}
                  label="Bank Code"
                  margin="dense"
                  required
                  helperText={errors.bankCode || ''}
                  error={!!errors.bankCode}
                />
              )}
            />

            <Autocomplete
              options={beneBankSetupOptions}
              getOptionLabel={option => option?.label || ''}
              value={
                beneBankSetupOptions.find(
                  o => o.value === formData.beneBankName
                ) || null
              }
              onChange={(_, newValue) =>
                handleBeneBankChange(newValue?.value || '')
              }
              fullWidth
              renderInput={params => (
                <TextField
                  {...params}
                  label="Bene Bank Name"
                  margin="dense"
                  required
                  helperText={errors.beneBankName || ''}
                  error={!!errors.beneBankName}
                />
              )}
            />

            <TextField
              label="Beneficiary Bank Account"
              value={formData.beneficiaryBankAccount}
              onChange={e =>
                handleChange('beneficiaryBankAccount', e.target.value)
              }
              fullWidth
              margin="dense"
              required
              autoComplete="off"
              helperText={errors.beneficiaryBankAccount || ''}
              error={!!errors.beneficiaryBankAccount}
            />

            <TextField
              label="IBG Bank Code"
              value={formData.ibgBankCode}
              fullWidth
              margin="dense"
              disabled
              InputLabelProps={{ shrink: !!formData.ibgBankCode }}
            />

            <Autocomplete
              options={paymentPurposeOptions}
              getOptionLabel={option => option?.label || ''}
              value={
                paymentPurposeOptions.find(
                  o => o.value === formData.defaultPaymentPurposeCode
                ) || null
              }
              onChange={(_, newValue) =>
                handleChange(
                  'defaultPaymentPurposeCode',
                  newValue?.value || ''
                )
              }
              fullWidth
              renderInput={params => (
                <TextField
                  {...params}
                  label="Default Payment Purpose Code"
                  margin="dense"
                />
              )}
            />
          </CardContents>
        ),
        footer: {
          actions: [
            {
              displayText: 'Cancel',
              props: {
                onClick: onClose,
                variant: 'contained',
                color: 'primary',
              },
            },
            {
              displayText: 'Confirm',
              props: {
                onClick: () => {
                  if (validate()) {
                    onConfirm(formData)
                  }
                },
                variant: 'contained',
                color: 'primary',
              },
            },
          ],
        },
      }}
    />
  )
}
